import type { DirectorConfig, DirectorRequestDetail } from '@/types/director'
import { ABSENCE_TYPE_LABELS, GATE_TRIGGER_LABELS } from '@/types/director'
import { lessonDaysCount } from '@/utils/caseList'
import { Info } from 'lucide-react'

interface CaseInfoLineProps {
  detail: DirectorRequestDetail
  config: DirectorConfig | null
}

/**
 * Fallakte v2 (P5.3): dynamische Infoline «Warum liegt dieser Fall bei dir?» —
 * je Gate-Trigger + Konfiguration zusammengesetzt (Konzept-B-Mockup).
 */
export function CaseInfoLine({ detail, config }: CaseInfoLineProps) {
  const triggers = ((detail.gate_reason as { triggers?: string[] } | null)?.triggers) ?? []
  const days = lessonDaysCount(detail.lessons, detail.date, detail.date_to)
  const threshold = config?.director_duration_threshold_days ?? null
  const absenceLabel = detail.absence_type_label
    || (detail.absence_type ? ABSENCE_TYPE_LABELS[detail.absence_type] ?? detail.absence_type : null)

  const reasons: string[] = []

  if (triggers.includes('absence_type') && absenceLabel) {
    reasons.push(`Die Abwesenheitsart «${absenceLabel}» ist genehmigungspflichtig — solche Anfragen pausieren für Ihre Entscheidung.`)
  }
  if (triggers.includes('duration')) {
    reasons.push(
      threshold != null
        ? `Die Abwesenheitsdauer (${days} ${days === 1 ? 'Tag' : 'Tage'}) erreicht Ihre Schwelle von ${threshold} Tagen — ab da entscheiden Sie.`
        : `Die Abwesenheitsdauer (${days} ${days === 1 ? 'Tag' : 'Tage'}) überschreitet die konfigurierte Schwelle.`,
    )
  }
  if (triggers.includes('lesson_account') || detail.account_hold) {
    reasons.push('Das Lektionenkonto der Lehrkraft ist erschöpft — weitere Absenzen blockieren bis zu Ihrer Freigabe.')
  }
  if (triggers.includes('no_substitute')) {
    reasons.push('Für die Anfrage konnte kein Ersatz gefunden werden (Eskalation) — Sie entscheiden über das weitere Vorgehen.')
  }
  if (detail.conditional_approval) {
    reasons.push('Genehmigt vorbehaltlich vollständiger Abdeckung — final wird die Vertretung erst, wenn alle Lektionen abgedeckt sind.')
  }

  if (reasons.length === 0) {
    reasons.push('Offene Zahler-Entscheidung — Sie legen fest, ob Schule oder Privat zahlt.')
  }

  const triggerChips = triggers
    .filter(t => GATE_TRIGGER_LABELS[t])
    .map(t => GATE_TRIGGER_LABELS[t])

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3" data-testid="case-info-line">
      <div className="flex items-start gap-2.5">
        <Info className="w-4 h-4 mt-0.5 text-indigo-600 shrink-0" />
        <div className="text-sm text-indigo-900 leading-relaxed min-w-0">
          <p className="font-semibold">Warum liegt dieser Fall bei dir?</p>
          <ul className="mt-1 space-y-1">
            {reasons.map((r, i) => (
              <li key={i} className="flex gap-1.5">
                <span aria-hidden className="text-indigo-400">•</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
          {triggerChips.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {triggerChips.map(c => (
                <span key={c} className="px-1.5 py-0.5 text-[10px] rounded bg-white/70 text-indigo-800 font-medium border border-indigo-200">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
